<template>
  <v-menu color="blue-gray" v-model="menu" :close-on-content-click="false" offset-y>
    <template v-slot:activator="{ on, attrs }">
      <span v-bind="attrs" v-on="on" class='multi-time'>{{ enabledTimesString }}</span>
    </template>
    <v-card>
      <v-list>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title>{{ $t('time_options_ui.title') }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-action>
            <v-btn text fab small @click="menu = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>
      </v-list>
      <v-divider></v-divider>
      <v-list>
        <v-list-item-group v-model="selectedTimes" multiple active-class="">
          <v-list-item v-for="(option) in timeOptions" :key="option.value" :value="option.value">
            <template v-slot:default="{ active }">
              <v-list-item-action>
                <v-checkbox :input-value="active"></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ option.text }}</v-list-item-title>
                <v-list-item-subtitle>{{ option.description }}</v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-list-item>
        </v-list-item-group>
        <v-list-item>
          <v-btn :color="showTimezones ? 'primary' : 'gray darken-2'" @click="togglTimeZoneDisplay()">{{ $t('time_options_ui.show_timezones') }}</v-btn>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script>
import Moment from 'moment-timezone'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import { each } from 'lodash'

/**
 * Basic re-implementation of the old MultiTimeDisplay. I think we can improve on this by just presenting all options
 * as a list of v-list-items with checkboxes. Right now there is a separate button for "show all timezones" which is
 * a bit strange, but to fix this would currently require changing how the old version works too.
 */
export default {
  name: 'MultiTimeDisplay',
  mixins: [
    CurrentUserMixin
  ],
  props: {
    /**
     * The time object to display
     */
    time: {
      type: String
    },
    /**
     * Optional field to set the timezone used. Otherwise will default to the customer context's timezone.
     */
    localTimezone: {
      type: String
    },
    /**
     * The Moment timezone format to use.
     */
    timeFormat: {
      type: String,
      default: 'lll'
    }
  },
  data () {
    return {
      // The boolean flag to control the v-menu component
      menu: false,
      // The time picker options
      timeOptions: [
        {
          text: this.$tc('customer'),
          value: 'customerTime',
          description: this.$t('time_options_ui.customer_description')
        },
        {
          text: this.$t('time_options_ui.utc_gmt'),
          value: 'utcTime',
          description: this.$t('time_options_ui.utc_description')
        },
        {
          text: this.$t('since_now'),
          value: 'relativeTime',
          description: this.$t('time_options_ui.relative_time_description')
        }
      ]
    }
  },
  computed: {
    /**
     * Render all times formats (slash seperated) which the user has enabled
     *
     * @return string
     */
    enabledTimesString () {
      const times = []
      each(this.timeFormatSettings.getEnabledTimeFormats(), format => {
        times.push(this[format])
      })
      // Default to displaying customer time if nothing is enabled.
      if (times.length === 0) {
        times.push(this.customerTime)
      }
      return times.join(' / ')
    },

    /**
     * @return array
     */
    timeFormatSettings () {
      return this.$store.state.localeModule.timeFormatSettings
    },

    /**
     * @return boolean
     */
    showTimezones () {
      return this.timeFormatSettings.showTimezones
    },

    /**
     * @return array
     */
    selectedTimes: {
      get: function () {
        return this.timeFormatSettings.getEnabledTimeFormats()
      },
      // When set also update the timeFormatSettings
      set: function (selectedTimes) {
        this.syncActiveTimeFormatSettings(selectedTimes)
      }
    },

    /**
     * Return a string representation of the timezone associated to the customer for the users current context.
     *
     * @return string
     */
    customerTimezone () {
      return this.localTimezone || this.customerContext.timezone() || 'UTC'
    },

    /**
     * The user can toggle the time zone display on or off. This function returns the format string which includes the
     * timezone (if toggled on)
     *
     * @return string
     */
    timeAndZoneFormat () {
      let format = this.timeFormat

      if (this.showTimezones) {
        format += ' zz'
      }

      return format
    },

    /**
     * Returns a string representation of the time adjusted to the customer's time zone
     *
     * @return string
     */
    customerTime () {
      return Moment(this.time).tz(this.customerTimezone).format(this.timeAndZoneFormat)
    },

    /**
     * Returns a string representation of the time in UTC
     *
     * @return string
     */
    utcTime () {
      return Moment(this.time).tz('UTC').format(this.timeAndZoneFormat)
    },

    /**
     * Returns a string representation of the time since now
     *
     * @return string
     */
    relativeTime () {
      return Moment(this.time).fromNow()
    }
  },
  methods: {
    /**
     * Fires an event to google analytics when the user changes their time display format.
     *
     * @param enabledFormats { Array } an array of formats that are enabled
     * @return undefined
     */
    fireGAChangeEvent (enabledFormats) {
      this.$gtag.event('update', { event_category: 'TimeDisplay', event_label: enabledFormats.join('-') })
    },

    /**
     * Parse the list of selected times, update the time format settings object
     * and dispatch the changes to the vuex store
     */
    syncActiveTimeFormatSettings (selectedTimes) {
      const timeFormatSettings = this.timeFormatSettings
      each(this.timeOptions, format => {
        timeFormatSettings[format.value] = selectedTimes.indexOf(format.value) > -1
      })
      this.$store.dispatch('localeModule/setTimeFormatSettings', timeFormatSettings)
    },

    /**
     * flip the showTimezones flag on the timeFormatSettings object and dispatch
     * the change to the vuex store
     */
    togglTimeZoneDisplay () {
      const timeFormatSettings = this.timeFormatSettings
      timeFormatSettings.showTimezones = !timeFormatSettings.showTimezones
      this.$store.dispatch('localeModule/setTimeFormatSettings', timeFormatSettings)
    }
  }
}
</script>

<style scoped>
.multi-time {
  cursor: pointer
}
</style>
